import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';
import { ChatWidgetTroubleshootingToolIssueType } from '@mm/state/interfaces/chatWidgetTroubleshooting';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import {
  chatWidgetTroubleshootingLastResultSelector,
  chatWidgetTroubleshootingLastResultTimeSelector,
} from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { getUniqId } from '@mm/utils';
import { Tooltip } from '@mm/components';
import classNames from 'classnames';
import moment from 'moment';
import React, { ReactNode, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './ChatWidgetTroubleshootingToolResult.pcss';
import { useChatbotActiveResult } from './hooks/use-chatbot-active-result';
import { useConsoleResult } from './hooks/use-console-result';
import {
  useDetectConflictingCSSForFBResult,
  useDetectConflictingCSSForWebchatResult,
} from './hooks/use-detect-conflicting-css';
import { useDetectedChatWidget } from './hooks/use-detected-chat-widget';
import { useDomainWhitelistedResult } from './hooks/use-domain-whitelisted-result';
import { useFBPageAgeRestrictions } from './hooks/use-fb-page-age-restrictions';
import { useFBPageCountryRestrictions } from './hooks/use-fb-page-country-restrictions';
import { useFBPageUnpublished } from './hooks/use-fb-page-unpublished';
import { useJSLoadedResult } from './hooks/use-js-loaded-result';
import { useJSTimestampResult } from './hooks/use-js-timestamp-result';
import { usePageFinishedLoadingResult } from './hooks/use-page-finished-loading-result';
import { usePromoterActiveResult } from './hooks/use-promoter-active-result';
import { useWpRocketCombinedJSResult } from './hooks/use-wp-rocket-combined-js-result';
import { useWrongFbSdkResult } from './hooks/use-wrong-fb-sdk-result';

export type ResultSet = {
  main: Result[];
  additional: Result[];
};

export enum ChatWidgetTroubleshootingToolResultTypes {
  ChatbotActiveResult = 'ChatbotActiveResult',
  FBPageUnpublished = 'FBPageUnpublished',
  PromoterActiveResult = 'PromoterActiveResult',
  FBPageCountryRestrictions = 'FBPageCountryRestrictions',
  FBPageAgeRestrictions = 'FBPageAgeRestrictions',
  WrongFbSdkResult = 'WrongFbSdkResult',
  WpRocketCombinedJSResult = 'WpRocketCombinedJSResult',
}

export type Result =
  | {
      type?: ChatWidgetTroubleshootingToolResultTypes;
      result: 'ok' | 'error' | 'info';
      description?: React.ReactNode;
      infoTooltip?: React.ReactNode;
    }
  | undefined;

type Props = {
  children?: ReactNode;
  ignoreList?: ChatWidgetTroubleshootingToolResultTypes[];
  hideContactHelp?: boolean;
  canCloseResults?: boolean;
  issueType: ChatWidgetTroubleshootingToolIssueType;
  promoter?: PromoterModel;
  runDiagnostic: (updatedPromoter?: PromoterModel) => Promise<void>;
  isXRay?: boolean;
  onClose?: () => void;
  css?: {
    container?: string;
    closeBtn?: string;
  };
};

export const ChatWidgetTroubleshootingToolResult = (props: Props) => {
  const {
    children,
    issueType,
    css,
    canCloseResults,
    onClose = () => {},
    ignoreList = [],
    hideContactHelp,
  } = props;
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);
  const dispatch = useDispatch();

  const resultSets: Record<ChatWidgetTroubleshootingToolIssueType, ResultSet> =
    {
      missing: {
        main: [
          usePageFinishedLoadingResult(),
          useJSLoadedResult(props),
          useChatbotActiveResult(props),
          usePromoterActiveResult(props),
          useFBPageUnpublished(props),
          useDomainWhitelistedResult(props),
          useJSTimestampResult(props),
        ],
        additional: [
          useFBPageCountryRestrictions(props),
          useFBPageAgeRestrictions(props),
          useDetectConflictingCSSForFBResult(props),
          useDetectConflictingCSSForWebchatResult(props),
          useWrongFbSdkResult(props),
          useWpRocketCombinedJSResult(),
          useDetectedChatWidget(),
          useConsoleResult(),
        ],
      },
      outdated: {
        main: [
          useJSLoadedResult(props),
          useJSTimestampResult(props),
          useChatbotActiveResult(props),
          usePromoterActiveResult(props),
        ],
        additional: [useDetectedChatWidget(), useWpRocketCombinedJSResult()],
      },
    };

  const resultTime = useSelector(
    chatWidgetTroubleshootingLastResultTimeSelector,
  );

  useEffect(() => {
    const compactedResultsList = [
      ...resultSets[issueType].main,
      ...resultSets[issueType].additional,
    ].filter(r => r);

    if (
      compactedResultsList.length !== 0 &&
      !compactedResultsList?.some(r => r?.result === 'error')
    ) {
      dispatch({
        type: 'CHATWIDGETTROUBLESHOOTING__NO_ISSUES_MOUNT',
      });
    }
  }, [result]);

  if (!result || !resultTime) {
    return null;
  }

  const renderAdditionalResults = (results: Result[]) => {
    if (results.filter(r => !!r).length === 0) {
      return null;
    }
    const anyErrors = !!results.find(r => r?.result === 'error');

    return (
      <>
        <div className="chat-widget-troubleshooting-tool-result__result">
          {anyErrors ? (
            <>
              <CloseCircleFilled className="chat-widget-troubleshooting-tool-result__result-fail" />
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                Additional errors found
              </span>
            </>
          ) : (
            <>
              <CheckCircleFilled className="chat-widget-troubleshooting-tool-result__result-sucess" />
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                Additional tests performed
              </span>
            </>
          )}
        </div>
        <div
          className={`chat-widget-troubleshooting-tool-result__additional-results ${
            anyErrors
              ? 'chat-widget-troubleshooting-tool-result__additional-results--error'
              : ''
          }`}
        >
          {renderResultArray(results)}
        </div>
      </>
    );
  };
  const renderResultArray = (results: Result[]) =>
    results
      .filter(
        v =>
          !ignoreList.includes(
            v?.type as ChatWidgetTroubleshootingToolResultTypes,
          ),
      )
      .map(result => {
        const infoTooltip = result?.infoTooltip ? (
          <Tooltip title={result.infoTooltip} placement="bottom">
            <i className="fa-question-circle far" />
          </Tooltip>
        ) : null;
        switch (result?.result) {
          case 'info':
            return (
              <div className="chat-widget-troubleshooting-tool-result__result">
                <CheckOutlined className="chat-widget-troubleshooting-tool-result__result-info" />
                <span className="chat-widget-troubleshooting-tool-result__result-description">
                  {result.description}
                  {infoTooltip}
                </span>
              </div>
            );
          case 'ok':
            return (
              <div className="chat-widget-troubleshooting-tool-result__result">
                <CheckCircleFilled className="chat-widget-troubleshooting-tool-result__result-sucess" />
                <span className="chat-widget-troubleshooting-tool-result__result-description">
                  {result.description}
                  {infoTooltip}
                </span>
              </div>
            );
          case 'error':
            return (
              <div
                className={classNames(
                  'chat-widget-troubleshooting-tool-result__result',
                  'chat-widget-troubleshooting-tool-result__result-error',
                )}
              >
                <CloseCircleFilled className="chat-widget-troubleshooting-tool-result__result-fail" />
                <span className="chat-widget-troubleshooting-tool-result__result-description">
                  {result.description}
                  {infoTooltip}
                </span>
              </div>
            );

          case undefined:
          default:
            return null;
        }
      });

  return (
    <div
      className={classNames(
        'chat-widget-troubleshooting-tool-result',
        css?.container,
      )}
    >
      {canCloseResults && (
        <CloseOutlined
          className={classNames(
            'chat-widget-troubleshooting-tool-result-header-close',
            css?.closeBtn,
          )}
          onClick={onClose}
        />
      )}
      <h1 className="chat-widget-troubleshooting-tool-result-header">
        Diagnostic Result
      </h1>
      <p className="chat-widget-troubleshooting-tool-result-calendar">
        {moment(resultTime).calendar()}
      </p>
      <div
        className="chat-widget-troubleshooting-tool-result__results"
        key={getUniqId()}
      >
        {[
          ...renderResultArray(resultSets[issueType].main),
          renderAdditionalResults(resultSets[issueType].additional),
          [
            ...resultSets[issueType].main,
            ...resultSets[issueType].additional,
          ].find(r => r?.result === 'error') ? null : hideContactHelp ? null : (
            <div className="chat-widget-troubleshooting-tool-result__result">
              <CheckCircleFilled className="chat-widget-troubleshooting-tool-result__result-sucess" />
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                &nbsp;Contact&nbsp;
                <a
                  href="mailto:support@customers.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support@customers.ai
                </a>
                &nbsp;if you still need help with your chat widget setup.
              </span>
            </div>
          ),
        ]}
      </div>
      {children}
    </div>
  );
};
